// frontend/src/app/components/projects/project-corpus/project-corpus.component.ts
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { InMemoryProjectCorpus } from '../../../project-corpus/adapters/in-memory-project-corpus';
import { HybridProjectCorpus } from '../../../project-corpus/adapters/hybrid-project-corpus';
import { ProjectCorpusService } from '../../../project-corpus/domain/project-corpus.service';
import { ImportJob, ProjectPaper, ProjectSummary } from '../../../project-corpus/domain/types';
import { PROJECT_CORPUS_PORT } from '../../../project-corpus/ports/project-corpus.port';
import { ProfileService } from '../../../services/profile.service';
import { ProjectService } from '../../../services/project.service';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { AddPapersDialogComponent } from './dialogs/add-papers-dialog.component';
import { AttachPdfDialogComponent } from './dialogs/attach-pdf-dialog.component';
import { EditMetadataDialogComponent } from './dialogs/edit-metadata-dialog.component';
import { JobDetailDialogComponent } from './dialogs/job-detail-dialog.component';
import { ImportJobStreamComponent } from './import-job-stream.component';
import { JobContextBarComponent } from './job-context-bar.component';
import { PaperListComponent } from './paper-list.component';
import { ProvenanceStripComponent } from './provenance-strip.component';
import { SearchRecordComponent } from '../search-record/search-record.component';
import { ProjectState, formatStageState, getStageStateClass } from '../../../dtos/project-state';
import { Project } from '../../../dtos/project';
import { ProjectProtocol } from '../../../dtos/project-protocol';
import { Group } from '../../../dtos/group';
import { GroupService } from '../../../services/group.service';
import { BaseAlertComponent } from '../../alerts/base-alert/base-alert.component';
import { ToastService } from '../../../services/toast.service';
import { httpErrorToMessage } from '../../../project-corpus/adapters/http-error-message';

/**
 * Project Corpus page container. Composes the provenance strip, the import-job
 * stream, the active-job context bar, and (in Phase 4) the paper list.
 *
 * Wiring:
 *  - Reads `:projectId` from the route, then calls `service.init(projectId)`.
 *  - Provides `ProjectCorpusService` + `{ PROJECT_CORPUS_PORT → InMemoryProjectCorpus }`
 *    at the component level — keeps the service feature-scoped (matching the
 *    `PdfCorpusService` pattern) and lets us swap to `HttpProjectCorpus` when
 *    the backend lands.
 */
@Component({
  selector: 'app-project-corpus',
  standalone: true,
  imports: [
    CommonModule,
    ProvenanceStripComponent,
    ImportJobStreamComponent,
    JobContextBarComponent,
    PaperListComponent,
    AddPapersDialogComponent,
    EditMetadataDialogComponent,
    AttachPdfDialogComponent,
    JobDetailDialogComponent,
    ConfirmModalComponent,
    BaseAlertComponent,
    SearchRecordComponent,
  ],
  providers: [
    ProjectCorpusService,
    InMemoryProjectCorpus,
    HybridProjectCorpus,
    { provide: PROJECT_CORPUS_PORT, useExisting: HybridProjectCorpus },
  ],
  templateUrl: './project-corpus.component.html',
  styleUrls: ['./project-corpus.component.scss'],
})
export class ProjectCorpusComponent implements OnInit, OnDestroy {
  protected group: Group | null = null;
  readonly ProjectState = ProjectState;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly service = inject(ProjectCorpusService);
  private readonly projectService = inject(ProjectService);
  private readonly profileService = inject(ProfileService);
  private readonly inMemoryAdapter = inject(InMemoryProjectCorpus);

  /** Full project details (for stage-nav and other UI needs). */
  readonly projectDetails = signal<Project | null>(null);
  /** Systematic review protocol (for readiness checks in the provenance strip). */
  readonly protocol = signal<ProjectProtocol | null>(null);

  /** Current user's role in this project ('PI' | 'RESEARCHER' | 'NONE'); null until loaded. */
  readonly projectRole = signal<string | null>(null);
  /** True when the current user is the PI of this project. */
  readonly isPi = computed(() => this.projectRole() === 'PI');
  /** True when the current user is a PI or researcher on this project. */
  readonly isProjectMember = computed(
    () => this.projectRole() === 'PI' || this.projectRole() === 'RESEARCHER',
  );
  /** Non-members (e.g. group viewers) get a read-only corpus with an explanatory notice. */
  readonly showNotMemberNotice = computed(() => this.projectRole() === 'NONE');
  /** Drives the "Start abstract screening" confirm dialog. */
  readonly searchRecordCollapsed = signal(false);
  readonly advanceConfirmOpen = signal(false);
  /** True when force-advancing (papers with incomplete metadata will be removed). */
  readonly isForceAdvance = computed(() => {
    const s = this.service.summary();
    return !!s && s.itemsNeedingAttention > 0;
  });
  /** Dialog title — uses "Force" when incomplete papers will be removed. */
  readonly advanceConfirmTitle = computed(() =>
    this.isForceAdvance() ? 'Force start abstract screening?' : 'Start abstract screening?',
  );
  /** Confirm button text. */
  readonly advanceConfirmButtonText = computed(() =>
    this.isForceAdvance() ? 'Force remove & start screening' : 'Start screening',
  );
  /** Plain-text message (fallback). */
  readonly advanceConfirmMessage = computed(() => {
    const p = this.projectDetails();
    const hasCriteria =
      this.hasCriteria(p?.inclusionCriteria) || this.hasCriteria(p?.exclusionCriteria);
    const base = this.isForceAdvance()
      ? `${this.service.summary()!.itemsNeedingAttention} paper${
          this.service.summary()!.itemsNeedingAttention === 1 ? '' : 's'
        } ${
          this.service.summary()!.itemsNeedingAttention === 1 ? 'is' : 'are'
        } missing title, abstract, or authors and will be removed. They will appear as "removed for other reasons" in the PRISMA diagram.`
      : 'All papers have title, abstract, and authors.';
    const closeMsg =
      'This closes ingestion. Papers can no longer be added or edited in the corpus.';
    const msg = `${closeMsg} ${base}`;
    if (!hasCriteria) {
      return `⚠️ Warning: No screening criteria have been defined. ${msg}`;
    }
    return msg;
  });
  /** HTML-formatted message shown in the force-advance confirm dialog. */
  readonly advanceConfirmMessageHtml = computed(() => {
    if (!this.isForceAdvance()) return null;
    const p = this.projectDetails();
    const hasCriteria =
      this.hasCriteria(p?.inclusionCriteria) || this.hasCriteria(p?.exclusionCriteria);
    const n = this.service.summary()!.itemsNeedingAttention;
    const plural = n === 1 ? '' : 's';

    let html = '';
    if (!hasCriteria) {
      html += `<p class="mb-2"><strong class="text-red-600">Warning:</strong> No screening criteria have been defined for this project.</p>`;
    }
    html += `<p class="mb-2"><strong>This closes ingestion.</strong> Papers can no longer be added or edited in the corpus.</p>`;
    html += `<div class="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm">`;
    html += `<p class="mb-1 font-semibold text-red-800">Force advance — what will happen:</p>`;
    html += `<ul class="list-disc pl-4 text-red-700 space-y-1">`;
    html += `<li><strong>${n}</strong> paper${plural} lacking title, abstract, or authors will be <strong>removed</strong> from the corpus.</li>`;
    html += `<li>They will appear as <em>"Removed for other reasons"</em> in the PRISMA flow diagram.</li>`;
    html += `<li>This action <strong>cannot be undone</strong></li>`;
    html += `</ul></div>`;
    return html;
  });

  private hasCriteria(val: string | undefined | null): boolean {
    if (!val) return false;
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed.length > 0;
      return false;
    } catch {
      return val.trim().length > 0;
    }
  }
  /** True while the advance request is in flight. */
  readonly advancing = signal(false);

  /** Drives the undo-import confirm dialog. */
  readonly undoConfirmJobId = signal<string | null>(null);
  readonly undoConfirmOpen = computed(() => this.undoConfirmJobId() !== null);
  /** True while the undo request is in flight. */
  readonly undoing = signal(false);

  /** Current user's ID, used to flag own import jobs with a "you" badge. */
  readonly currentUserId = toSignal(
    this.profileService.currentUser$.pipe(map((u) => (u?.id != null ? String(u.id) : ''))),
    { initialValue: '' },
  );

  readonly projectId = signal<string>('');
  /** Drives the Add-papers wizard dialog visibility. */
  readonly addPapersOpen = signal(false);
  /** Drives the edit-metadata dialog; null when closed. */
  readonly editingPaper = signal<ProjectPaper | null>(null);
  /** Drives the view-only detail dialog; null when closed. */
  readonly viewingPaper = signal<ProjectPaper | null>(null);
  /** Drives the attach-PDF dialog; null when closed. */
  readonly attachingPaper = signal<ProjectPaper | null>(null);
  /** Drives the job-detail modal; null when closed. */
  readonly detailJob = signal<ImportJob | null>(null);

  constructor(
    private groupService: GroupService,
    private toastService: ToastService,
  ) {}

  /**
   * Derives the currently-active job from the importJobs page using the
   * service's `activeJobId` signal. Used to render the context bar.
   */
  readonly activeJob = computed<ImportJob | null>(() => {
    const id = this.service.activeJobId();
    if (!id) return null;
    const page = this.service.importJobs();
    if (!page) return null;
    return page.items.find((j) => j.id === id) ?? null;
  });

  readonly runningImportJobIds = computed<readonly string[]>(() => {
    const page = this.service.importJobs();
    return page?.items.filter((job) => job.status === 'RUNNING').map((job) => job.id) ?? [];
  });

  readonly displayedSummary = computed<ProjectSummary | null>(() => {
    const summary = this.service.summary();
    if (!summary) return null;
    if (this.runningImportJobIds().length === 0) return summary;
    return { ...summary, itemsNeedingAttention: 0 };
  });

  /**
   * The corpus is editable (add/edit papers) only during SETUP and INGESTION,
   * for any project member. Past that the page is read-only for everyone.
   */
  readonly isCorpusEditable = computed(() => {
    const s = this.service.summary()?.state;
    return this.isProjectMember() && (s === 'SETUP' || s === 'INGESTION');
  });

  /** Hides "+ Add papers" once past SETUP/INGESTION lifecycle states. */
  readonly canAddPapers = this.isCorpusEditable;

  /**
   * The advance button shows only for the PI while the corpus is still editable
   * (SETUP or INGESTION). Pressing it ends that phase and starts abstract screening.
   */
  readonly showAdvanceButton = computed(() => this.isPi() && this.isCorpusEditable());

  private routeSub?: Subscription;

  ngOnInit(): void {
    this.routeSub = this.route.parent?.params.subscribe((params) => {
      const id = params['projectId'];
      if (!id) {
        this.router.navigate(['/projects']);
        return;
      }
      this.projectId.set(id);
      // Seed the in-memory adapter with the correct project id so the page
      // has papers to render. When the backend lands and we swap to
      // HttpProjectCorpus, this seeding becomes a no-op.
      this.inMemoryAdapter.seedDefault(id);
      this.service.init(id);
      // Fetch full project details for stage-nav component
      this.projectService.getProjectById(id).subscribe({
        next: (project) => {
          if (project) {
            this.projectDetails.set(project);
            this.groupService.getGroupById(project?.groupId).subscribe({
              next: (group) => {
                this.group = group;
              },
              error: (err: any) => {
                console.error('Failed to load group details', err);
              },
            });
          }
        },
        error: () => this.projectDetails.set(null),
      });
      this.projectService.getProjectProtocol(id).subscribe({
        next: (prot) => this.protocol.set(prot),
        error: () => this.protocol.set(null),
      });
      this.projectService.getUserProjectRole(id).subscribe({
        next: (role) => this.projectRole.set(role.role),
        error: () => this.projectRole.set(null),
      });
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  onJobClick(jobId: string | null): void {
    this.service.setActiveJobFilter(jobId);
  }

  onTitleChange(event: { jobId: string; title: string }): void {
    this.service.updateTitle(event.jobId, event.title).subscribe({
      error: (err) => console.error('Failed to update job title', err),
    });
  }

  onUndoImport(jobId: string): void {
    this.undoConfirmJobId.set(jobId);
  }

  onUndoConfirmOpenChange(open: boolean): void {
    if (!open) this.undoConfirmJobId.set(null);
  }

  onUndoConfirmed(): void {
    const jobId = this.undoConfirmJobId();
    if (!jobId) return;
    this.undoing.set(true);
    this.service.undoImportJob(jobId).subscribe({
      next: () => {
        this.undoing.set(false);
        this.undoConfirmJobId.set(null);
      },
      error: () => {
        this.undoing.set(false);
        this.undoConfirmJobId.set(null);
      },
    });
  }

  onJobDetail(id: string): void {
    this.detailJob.set(this.service.importJobs()?.items.find((j) => j.id === id) ?? null);
  }

  /**
   * Editing a paper from inside the job-detail modal: close that modal first so
   * the edit dialog is not rendered behind it (both share the same z-layer).
   */
  onEditPaperFromJob(paper: ProjectPaper): void {
    // Read-only once ingestion is closed: don't open the editor for anyone.
    if (!this.isCorpusEditable()) {
      return;
    }
    this.detailJob.set(null);
    this.editingPaper.set(paper);
  }

  onNeedsAttentionToggle(): void {
    const current = this.service.currentFilter();
    if (this.runningImportJobIds().length > 0) {
      return;
    }
    const next = current.status === 'NEEDS_ATTENTION' ? { ...current } : { ...current };
    if (current.status === 'NEEDS_ATTENTION') {
      delete (next as { status?: string }).status;
    } else {
      (next as { status?: string }).status = 'NEEDS_ATTENTION';
    }
    this.service.setFilter(next);
  }

  onAdvanceClick(): void {
    this.advanceConfirmOpen.set(true);
  }

  onAdvanceConfirmOpenChange(open: boolean): void {
    this.advanceConfirmOpen.set(open);
  }

  onAdvanceConfirmed(): void {
    this.advancing.set(true);
    this.service.advanceToScreening(this.isForceAdvance()).subscribe({
      next: () => {
        this.advancing.set(false);
        this.advanceConfirmOpen.set(false);
        this.projectService.notifyProjectUpdated();
        this.router.navigate(['/projects', this.projectId(), 'title-abstract-screening']);
      },
      error: () => {
        this.advancing.set(false);
        this.advanceConfirmOpen.set(false);
      },
    });
  }

  openAddPapers(): void {
    this.addPapersOpen.set(true);
  }

  onAddPapersOpenChange(open: boolean): void {
    this.addPapersOpen.set(open);
  }

  onEditingPaperOpenChange(open: boolean): void {
    if (!open) this.editingPaper.set(null);
  }

  onAttachingPaperOpenChange(open: boolean): void {
    if (!open) this.attachingPaper.set(null);
  }

  getBadgeClass(state: string): string {
    return getStageStateClass(state);
  }

  formatState(state: string): string {
    return formatStageState(state);
  }
}
